import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { translateLocation } from '../../data/locationTranslations';
import { locationData } from '../../data/locationData';
import { toast } from 'react-toastify';
import {
  Mail, Lock, Eye, EyeOff, User, ArrowRight, ArrowLeft, Check,
  Calendar, Clock, MapPin, Briefcase, GraduationCap, FileText, Users,
  ChevronDown, Sparkles, X
} from 'lucide-react';
import DatePickerInput from '../../components/common/DatePickerInput';
import TimePickerInput from '../../components/common/TimePickerInput';
import { applyTheme, loadTheme } from '../../utils/theme';
import { L, kicker as luxeKicker, display, btn, LuxeStyles, useIsDesktop } from '../../theme/luxe';

// Small/secondary text is never lighter than #6E685F (matches the approved artboard).
const MUTE = '#6E685F';
const kicker = (extra = {}) => luxeKicker({ color: MUTE, ...extra });

// Per-referral theme overrides for the register landing page.
const REF_THEMES = {
  '39': { primary: '#454545', secondary: '#FF8FF0', button: '#DB57FF' },
};

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const { language, setLanguage: switchLanguage } = useLanguage();
  useEffect(() => {
    if (!sessionStorage.getItem('auth_lang_touched')) {
      sessionStorage.setItem('auth_lang_touched', '1');
      if (language !== 'zh') switchLanguage('zh');
    }
  }, []); // eslint-disable-line
  // Per-referral theme override (e.g. ?ref=39). Restores the normal theme on unmount.
  useEffect(() => {
    const refTheme = REF_THEMES[searchParams.get('ref')];
    if (refTheme) {
      applyTheme(refTheme);
      return () => applyTheme(loadTheme());
    }
  }, [searchParams]);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    birthday: '',
    birthTime: '',
    gender: '',
    country: '',
    state: '',
    city: '',
    occupation: '',
    education: '',
    bio: '',
    referral_code: searchParams.get('ref') || '',
    agreedToTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [direction, setDirection] = useState('next');
  // Searchable city combobox state. The input doubles as the search box and
  // the committed value; the dropdown is just a quick-pick list filtered to
  // cities in the selected state (free-text custom entries still accepted).
  const [cityFocused, setCityFocused] = useState(false);
  const [occupationFocused, setOccupationFocused] = useState(false);
  const [generatingBio, setGeneratingBio] = useState(false);
  // Signature modal — the terms checkbox can only be checked by actually
  // signing the agreement (same flow as the matchmaker partner-agreement).
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureStyle, setSignatureStyle] = useState('');
  const [signatureData, setSignatureData] = useState(null);
  const inputRef = useRef(null);

  // Layout breakpoint (hook must run before the early redirect below).
  const isDesktop = useIsDesktop();

  // Focus input when step changes
  useEffect(() => {
    if (inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [currentStep]);

  // Redirect matchmaker registrations to dedicated page
  const regType = searchParams.get('type');
  if (regType === 'external_matchmaker' || regType === 'matchmaker') {
    return <Navigate to={`/register/matchmaker?type=${regType}${searchParams.get('ref') ? '&ref=' + searchParams.get('ref') : ''}`} replace />;
  }

  // Country data with states/provinces
  const countries = [
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'AU', name: 'Australia' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'CN', name: 'China' },
    { code: 'JP', name: 'Japan' },
    { code: 'KR', name: 'South Korea' },
    { code: 'SG', name: 'Singapore' },
    { code: 'HK', name: 'Hong Kong' },
    { code: 'TW', name: 'Taiwan' },
    { code: 'IN', name: 'India' },
    { code: 'BR', name: 'Brazil' },
    { code: 'MX', name: 'Mexico' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'SE', name: 'Sweden' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'TH', name: 'Thailand' },
    { code: 'PH', name: 'Philippines' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'VN', name: 'Vietnam' },
    { code: 'OTHER', name: 'Other' },
  ];

  const statesByCountry = {
    US: ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'],
    CA: ['Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador', 'Nova Scotia', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan'],
    GB: ['England', 'Scotland', 'Wales', 'Northern Ireland'],
    AU: ['New South Wales', 'Victoria', 'Queensland', 'Western Australia', 'South Australia', 'Tasmania', 'Northern Territory', 'Australian Capital Territory'],
    CN: ['Beijing', 'Shanghai', 'Guangdong', 'Zhejiang', 'Jiangsu', 'Sichuan', 'Shandong', 'Henan', 'Hubei', 'Hunan', 'Fujian', 'Anhui', 'Liaoning', 'Shaanxi', 'Chongqing', 'Tianjin', 'Other'],
    DE: ['Bavaria', 'Berlin', 'Hamburg', 'Hesse', 'North Rhine-Westphalia', 'Baden-Württemberg', 'Lower Saxony', 'Saxony', 'Other'],
    FR: ['Île-de-France', 'Provence-Alpes-Côte d\'Azur', 'Auvergne-Rhône-Alpes', 'Nouvelle-Aquitaine', 'Occitanie', 'Other'],
    JP: ['Tokyo', 'Osaka', 'Kanagawa', 'Aichi', 'Fukuoka', 'Hokkaido', 'Kyoto', 'Hyogo', 'Other'],
    IN: ['Maharashtra', 'Delhi', 'Karnataka', 'Tamil Nadu', 'Telangana', 'Gujarat', 'West Bengal', 'Uttar Pradesh', 'Other'],
  };

  // Local bilingual helper — keeps the step config terse.
  const tr = (zh, en) => (language === 'zh' ? zh : en);

  const genderOptions = [
    { value: 'male',   label: tr('男', 'Male') },
    { value: 'female', label: tr('女', 'Female') },
  ];

  // 「副学士」「学士」合并为「大学」(college). Old profile values that come
  // back as `associates` / `bachelors` from saved sessions still resolve to
  // the College option via the value `college`.
  const educationOptions = [
    { value: 'high_school',  label: tr('高中', 'High School') },
    { value: 'some_college', label: tr('大学未毕业', 'Some College') },
    { value: 'college',      label: tr('大学', 'College') },
    { value: 'masters',      label: tr('硕士', "Master's Degree") },
    { value: 'doctorate',    label: tr('博士', 'Doctorate / PhD') },
    { value: 'professional', label: tr('专业学位 (MD / JD 等)', 'Professional Degree (MD, JD, etc.)') },
    { value: 'other',        label: tr('其他', 'Other') },
  ];

  // Searchable occupation options — used by the new "occupation" step type
  // (text input with autocomplete dropdown; free-text fallback accepted).
  const occupationOptions = [
    '软件工程师/Software Engineer', '产品经理/Product Manager', '设计师/Designer', '数据科学家/Data Scientist',
    '医生/Doctor', '护士/Nurse', '牙医/Dentist', '药剂师/Pharmacist', '心理咨询师/Psychologist',
    '律师/Lawyer', '会计师/Accountant', '审计师/Auditor', '金融分析师/Financial Analyst',
    '银行家/Banker', '投资人/Investor', '保险代理/Insurance Agent',
    '教师/Teacher', '教授/Professor', '研究员/Researcher', '学生/Student',
    '企业家/Entrepreneur', '创始人/Founder', '高管/Executive', '经理/Manager', '销售/Sales',
    '市场营销/Marketing', '人力资源/Human Resources', '行政/Administration',
    '工程师/Engineer (Civil)', '工程师/Engineer (Mechanical)', '工程师/Engineer (Electrical)',
    '建筑师/Architect', '室内设计师/Interior Designer',
    '艺术家/Artist', '音乐家/Musician', '摄影师/Photographer', '作家/Writer', '记者/Journalist',
    '演员/Actor', '导演/Director', '编辑/Editor',
    '厨师/Chef', '餐饮经营/Restaurant Owner',
    '房地产经纪/Real Estate Agent', '房地产开发/Real Estate Developer',
    '工程师/Engineer (Software)', '运营/Operations',
    '律师助理/Paralegal', '法务/Legal Counsel',
    '空乘/Flight Attendant', '飞行员/Pilot',
    '警察/Police Officer', '军人/Military',
    '红娘/Matchmaker',
    '自由职业/Freelancer', '咨询顾问/Consultant',
    '其他/Other',
  ];

  // Calculate zodiac sign from birthday
  const getZodiacSign = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    
    const signs = [
      { sign: 'Capricorn', symbol: '♑', start: [1, 1], end: [1, 19] },
      { sign: 'Aquarius', symbol: '♒', start: [1, 20], end: [2, 18] },
      { sign: 'Pisces', symbol: '♓', start: [2, 19], end: [3, 20] },
      { sign: 'Aries', symbol: '♈', start: [3, 21], end: [4, 19] },
      { sign: 'Taurus', symbol: '♉', start: [4, 20], end: [5, 20] },
      { sign: 'Gemini', symbol: '♊', start: [5, 21], end: [6, 20] },
      { sign: 'Cancer', symbol: '♋', start: [6, 21], end: [7, 22] },
      { sign: 'Leo', symbol: '♌', start: [7, 23], end: [8, 22] },
      { sign: 'Virgo', symbol: '♍', start: [8, 23], end: [9, 22] },
      { sign: 'Libra', symbol: '♎', start: [9, 23], end: [10, 22] },
      { sign: 'Scorpio', symbol: '♏', start: [10, 23], end: [11, 21] },
      { sign: 'Sagittarius', symbol: '♐', start: [11, 22], end: [12, 21] },
      { sign: 'Capricorn', symbol: '♑', start: [12, 22], end: [12, 31] },
    ];

    for (const zodiac of signs) {
      const [startMonth, startDay] = zodiac.start;
      const [endMonth, endDay] = zodiac.end;
      
      if ((month === startMonth && day >= startDay) || (month === endMonth && day <= endDay)) {
        return zodiac;
      }
    }
    return null;
  };

  // Calculate age from birthday
  const calculateAge = (dateStr) => {
    if (!dateStr) return null;
    const today = new Date();
    const birthDate = new Date(dateStr);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const steps = [
    {
      id: 'firstName',
      question:  tr('您的名是？',          "What's your first name?"),
      subtitle:  tr('我们先从基本信息开始', "Let's start with the basics"),
      field: 'firstName', type: 'text',
      placeholder: tr('请输入您的名...', 'Your first name...'),
      icon: User,
      validation: (value) => {
        if (!value.trim()) return tr('请输入您的名', 'Please enter your first name');
        if (value.trim().length < 2) return tr('名至少需要 2 个字符', 'First name must be at least 2 characters');
        return null;
      }
    },
    {
      id: 'lastName',
      question:  tr('您的姓是？',          'And your last name?'),
      subtitle:  tr('基本信息马上完成',     'Almost there with the basics'),
      field: 'lastName', type: 'text',
      placeholder: tr('请输入您的姓...', 'Your last name...'),
      icon: User,
      validation: (value) => {
        if (!value.trim()) return tr('请输入您的姓', 'Please enter your last name');
        if (value.trim().length < 2) return tr('姓至少需要 2 个字符', 'Last name must be at least 2 characters');
        return null;
      }
    },
    {
      id: 'email',
      question:  tr('您的邮箱？',          "What's your email?"),
      subtitle:  tr('用于您的账号登录',     "We'll use this for your account"),
      field: 'email', type: 'email',
      placeholder: 'name@example.com',
      icon: Mail,
      validation: (value) => {
        if (!value.trim()) return tr('请输入邮箱', 'Please enter your email');
        if (!/\S+@\S+\.\S+/.test(value)) return tr('请输入有效的邮箱地址', 'Please enter a valid email address');
        return null;
      }
    },
    {
      id: 'password',
      question:  tr('设置密码',              'Create a password'),
      subtitle:  tr('请设置一个安全易记的密码', 'Make it strong and memorable'),
      field: 'password', type: 'password',
      placeholder: tr('至少 8 个字符...', 'At least 8 characters...'),
      icon: Lock,
      validation: (value) => {
        if (!value) return tr('请输入密码', 'Please enter a password');
        if (value.length < 8) return tr('密码至少需要 8 个字符', 'Password must be at least 8 characters');
        return null;
      }
    },
    {
      id: 'confirmPassword',
      question:  tr('确认密码',         'Confirm your password'),
      subtitle:  tr('再输入一次',       'Just to make sure'),
      field: 'confirmPassword', type: 'password',
      placeholder: tr('请再次输入密码...', 'Re-enter your password...'),
      icon: Lock,
      validation: (value) => {
        if (!value) return tr('请确认密码', 'Please confirm your password');
        if (value !== formData.password) return tr('两次输入的密码不一致', 'Passwords do not match');
        return null;
      }
    },
    {
      id: 'birthday',
      question:  tr('您的生日是哪天？',        "When's your birthday?"),
      subtitle:  tr('帮助我们为您匹配合适的对象', 'This helps us find compatible matches'),
      field: 'birthday', type: 'date',
      icon: Calendar,
      validation: (value) => {
        if (!value) return tr('请输入您的生日', 'Please enter your birthday');
        const age = calculateAge(value);
        if (age < 18) return tr('您需要年满 18 周岁', 'You must be at least 18 years old');
        if (age > 120) return tr('请输入有效的日期', 'Please enter a valid date');
        return null;
      }
    },
    {
      id: 'birthTime',
      question:  tr('您出生的时间？',                  "What time were you born?"),
      subtitle:  tr('选填 — 用于精准星座 (24 小时制)', 'Optional - for detailed astrology (24-hour format)'),
      field: 'birthTime', type: 'time',
      icon: Clock, optional: true,
      validation: () => null
    },
    {
      id: 'gender',
      question:  tr('您的性别？',                'What\'s your gender?'),
      subtitle:  tr('选择最符合您的选项',         'Select the option that best describes you'),
      field: 'gender', type: 'select', options: genderOptions, icon: Users,
      validation: (value) => {
        if (!value) return tr('请选择您的性别', 'Please select your gender');
        return null;
      }
    },
    {
      id: 'location',
      question:  tr('您居住在哪里？',         "Where do you live?"),
      subtitle:  tr('帮我们找到您附近的匹配', 'Help us find matches near you'),
      field: 'location', type: 'location', icon: MapPin,
      validation: () => {
        if (!formData.country) return tr('请选择您所在的国家', 'Please select your country');
        return null;
      }
    },
    {
      id: 'occupation',
      question:  tr('您的职业是什么？',       'What do you do for work?'),
      subtitle:  tr('搜索或选择您的职业',     'Search or pick your profession'),
      field: 'occupation', type: 'occupation',
      placeholder: tr('搜索职业，如：软件工程师 / 老师...', 'Search a profession, e.g. Software Engineer, Teacher...'),
      icon: Briefcase,
      validation: (value) => {
        if (!value.trim()) return tr('请输入您的职业', 'Please enter your occupation');
        return null;
      }
    },
    {
      id: 'education',
      question:  tr('您的最高学历？',     "What's your education level?"),
      subtitle:  tr('请选择您的学历层次', 'Your highest level of education'),
      field: 'education', type: 'select', options: educationOptions, icon: GraduationCap,
      validation: (value) => {
        if (!value) return tr('请选择您的学历', 'Please select your education level');
        return null;
      }
    },
    {
      id: 'bio',
      question:  tr('介绍一下自己',     'Tell us about yourself'),
      subtitle:  tr('写一段简短自我介绍 (选填)', 'Write a short bio (optional)'),
      field: 'bio', type: 'textarea',
      placeholder: tr('分享一下您的兴趣爱好以及希望寻找的对象...', "Share a bit about yourself, your interests, and what you're looking for..."),
      icon: FileText,
      // Bio is still effectively optional (validation always passes), but the
      // CTA reads "继续" instead of "跳过" per request.
      validation: () => null
    },
    {
      id: 'terms',
      question:  tr('就快完成啦！',        'Almost there!'),
      subtitle:  tr('请阅读并同意条款',     'Review and accept our terms'),
      field: 'agreedToTerms', type: 'checkbox',
      validation: (value) => {
        if (!value) return tr('请先同意条款再继续', 'Please agree to the terms to continue');
        return null;
      }
    }
  ];


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const validateCurrentStep = () => {
    const step = steps[currentStep];
    if (step.type === 'location') {
      const error = step.validation();
      if (error) {
        setErrors({ ...errors, location: error });
        return false;
      }
    } else {
      const error = step.validation(formData[step.field]);
      if (error) {
        setErrors({ ...errors, [step.field]: error });
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (!validateCurrentStep()) return;
    
    if (currentStep < steps.length - 1) {
      setDirection('next');
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setDirection('prev');
      setCurrentStep(currentStep - 1);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !['checkbox', 'textarea', 'location', 'occupation'].includes(steps[currentStep].type)) {
      e.preventDefault();
      if (currentStep === steps.length - 1) {
        handleSubmit(e);
      } else {
        handleNext();
      }
    }
  };

  // AI-generate a self-introduction from whatever the user has filled so far.
  // Posts to the public /api/public/generate-bio endpoint (no auth needed —
  // they haven't signed up yet). Merges in the current bio draft as
  // additional context.
  const handleGenerateBio = async () => {
    if (generatingBio) return;
    setGeneratingBio(true);
    try {
      const zodiac = formData.birthday ? getZodiacSign(formData.birthday) : null;
      const countryName = (countries.find(c => c.code === formData.country) || {}).name;
      const customValues = {};
      if (zodiac) customValues[language === 'zh' ? '星座' : 'Star Sign'] = `${zodiac.symbol} ${zodiac.sign}`;
      if (formData.birthTime) customValues[language === 'zh' ? '出生时间' : 'Birth Time'] = formData.birthTime;
      if (formData.education) {
        const eduLabel = (educationOptions.find(o => o.value === formData.education) || {}).label || formData.education;
        customValues[language === 'zh' ? '学历' : 'Education'] = eduLabel;
      }
      if (formData.bio && formData.bio.trim()) {
        customValues[language === 'zh' ? '用户已写的自我介绍草稿' : "User's current bio draft"] = formData.bio.trim();
      }

      const apiBase = process.env.REACT_APP_API_URL || '/api';
      const resp = await fetch(`${apiBase}/public/generate-bio`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.firstName || '',
          last_name:  formData.lastName  || '',
          gender:     formData.gender    || '',
          occupation: formData.occupation || '',
          city:       formData.city      || '',
          state:      formData.state     || '',
          country:    countryName        || '',
          language,
          customValues,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.bio) {
        toast.error(data.error || tr('AI 生成失败，请重试', 'AI generation failed, please retry'));
        return;
      }
      setFormData({ ...formData, bio: data.bio });
      toast.success(tr('已生成', 'Generated'));
    } catch (e) {
      toast.error(tr('AI 生成失败，请重试', 'AI generation failed, please retry'));
    } finally {
      setGeneratingBio(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateCurrentStep()) return;

    setLoading(true);

    const zodiac = getZodiacSign(formData.birthday);
    const age = calculateAge(formData.birthday);

    const result = await register({
      name: `${formData.firstName} ${formData.lastName}`.trim(),
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      password: formData.password,
      birthday: formData.birthday,
      birth_time: formData.birthTime || null,
      age: age,
      zodiac_sign: zodiac?.sign || null,
      gender: formData.gender,
      country: formData.country,
      state: formData.state,
      city: formData.city,
      location: [formData.city, formData.state, formData.country].filter(Boolean).join(', '),
      occupation: formData.occupation,
      education: formData.education,
      bio: formData.bio,
      referral_code: formData.referral_code || undefined,
    });

    if (result.success) {
      toast.success('Account created successfully!');
      navigate('/');
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  const currentStepData = steps[currentStep];
  const progress = ((currentStep + 1) / steps.length) * 100;
  const zodiac = getZodiacSign(formData.birthday);
  const age = calculateAge(formData.birthday);

  // ─── Presentation helpers (Luxe editorial) ──────────────────────────────────
  const pad2 = (n) => String(n).padStart(2, '0');
  const stepLabels = {
    firstName:       tr('名', 'First name'),
    lastName:        tr('姓', 'Last name'),
    email:           tr('邮箱', 'Email'),
    password:        tr('密码', 'Password'),
    confirmPassword: tr('确认密码', 'Confirm'),
    birthday:        tr('生日', 'Birthday'),
    birthTime:       tr('出生时间', 'Birth time'),
    gender:          tr('性别', 'Gender'),
    location:        tr('居住地', 'Residence'),
    occupation:      tr('职业', 'Profession'),
    education:       tr('学历', 'Education'),
    bio:             tr('自我介绍', 'About you'),
    terms:           tr('签署协议', 'Agreement'),
  };
  const labelOf = (s) => stepLabels[s.id] || s.id;

  const errLine = (msg) => (msg ? (
    <div role="alert" className="lx-fade" style={{
      fontFamily: L.sans, fontSize: 13, color: L.danger, marginTop: 12,
      letterSpacing: '.02em', display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <span style={{ width: 14, height: 1, background: L.danger, flexShrink: 0 }} />
      {msg}
    </div>
  ) : null);

  const fieldKicker = (text, extra = {}) => (
    <div style={kicker({ fontSize: 12, color: MUTE, marginBottom: 2, ...extra })}>{text}</div>
  );

  const bigInput = (hasErr, extra = {}) => ({
    fontFamily: L.serif, fontSize: isDesktop ? 28 : 22, fontWeight: 400, padding: '12px 0',
    borderBottomColor: hasErr ? L.danger : L.ink,
    ...extra,
  });

  const selectChevron = (
    <ChevronDown size={16} strokeWidth={1.25} style={{
      position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)',
      pointerEvents: 'none', color: MUTE,
    }} />
  );

  const dropdownBox = {
    position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
    background: L.card, border: '1px solid rgba(17,17,17,.2)', borderRadius: L.radius,
    boxShadow: 'none', overflowY: 'auto', zIndex: 30,
  };
  const dropdownRow = {
    padding: '12px 16px', cursor: 'pointer', fontFamily: L.sans, fontSize: 14,
    color: L.ink, borderBottom: `1px solid ${L.lineSoft}`,
  };

  const pickerInputStyle = (hasErr) => ({
    padding: '14px 32px 14px 0', fontSize: 19, fontWeight: 300, border: 0,
    borderBottom: `1px solid ${hasErr ? L.danger : L.line}`, borderRadius: 0,
    background: 'transparent', fontFamily: L.sans, color: L.ink,
  });

  const renderInputField = () => {
    const fieldErr = errors[currentStepData.field];
    switch (currentStepData.type) {
      case 'select':
        return (
          <div>
            <div role="radiogroup" aria-label={currentStepData.question} style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {currentStepData.options.map((opt) => {
                const on = formData[currentStepData.field] === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    className={`lx-chip${on ? ' lx-on' : ''}`}
                    onClick={() => handleChange({ target: { name: currentStepData.field, value: opt.value, type: 'radio' } })}
                    style={{
                      padding: '12px 22px', fontSize: 14, borderRadius: L.radius,
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      ...(fieldErr && !on ? { borderColor: 'rgba(158,43,37,.35)' } : {}),
                    }}
                  >
                    {on && <Check size={13} strokeWidth={1.5} />}
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {errLine(fieldErr)}
          </div>
        );

      case 'location':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
            {/* Country */}
            <div>
              {fieldKicker(tr('国家 / 地区', 'Country'))}
              <div style={{ position: 'relative' }}>
                <select
                  name="country"
                  className="lx-input"
                  value={formData.country}
                  onChange={(e) => {
                    setFormData({ ...formData, country: e.target.value, state: '', city: '' });
                  }}
                  style={bigInput(!!errors.location && !formData.country, { fontFamily: L.sans, fontWeight: 300, fontSize: 17, color: formData.country ? L.ink : MUTE })}
                >
                  <option value="">{language === 'zh' ? '选择国家...' : 'Select Country...'}</option>
                  {countries.map(c => (
                    <option key={c.code} value={c.code}>{translateLocation(c.name, language)}</option>
                  ))}
                </select>
                {selectChevron}
              </div>
            </div>

            {/* State */}
            {formData.country && statesByCountry[formData.country] && (
              <div className="lx-fade-up">
                {fieldKicker(tr('省 / 州', 'State / Province'))}
                <div style={{ position: 'relative' }}>
                  <select
                    name="state"
                    className="lx-input"
                    value={formData.state}
                    onChange={handleChange}
                    style={bigInput(false, { fontFamily: L.sans, fontWeight: 300, fontSize: 17, color: formData.state ? L.ink : MUTE })}
                  >
                    <option value="">{language === 'zh' ? '选择省/州...' : 'Select State/Province...'}</option>
                    {statesByCountry[formData.country]?.map(s => (
                      <option key={s} value={s}>{translateLocation(s, language)}</option>
                    ))}
                  </select>
                  {selectChevron}
                </div>
              </div>
            )}

            {/* City — searchable combobox scoped to the selected state's
                cities (free-text custom entries still allowed). Cities are
                rendered in Chinese via translateLocation. */}
            {formData.country && (() => {
              const countryName = (countries.find(c => c.code === formData.country) || {}).name;
              const stateCities = (countryName && formData.state && locationData[countryName] && locationData[countryName][formData.state]) || [];
              const q = String(formData.city || '').trim().toLowerCase();
              const matches = q
                ? stateCities.filter(ct =>
                    ct.toLowerCase().includes(q) ||
                    translateLocation(ct, 'zh').toLowerCase().includes(q))
                : stateCities;
              const showDropdown = cityFocused && stateCities.length > 0
                && !(matches.length === 1 && matches[0].toLowerCase() === q);

              return (
                <div className="lx-fade-up lx-d1">
                  {fieldKicker(tr('城市', 'City'))}
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      name="city"
                      className="lx-input"
                      value={formData.city}
                      onChange={handleChange}
                      onFocus={() => setCityFocused(true)}
                      // Delay so the option's onMouseDown fires before blur hides it.
                      onBlur={() => setTimeout(() => setCityFocused(false), 150)}
                      placeholder={
                        !formData.state
                          ? (language === 'zh' ? '请先选择省/州...' : 'Select a state first...')
                          : (language === 'zh' ? '搜索或输入城市...' : 'Search or type your city...')
                      }
                      autoComplete="off"
                      style={bigInput(false, { fontFamily: L.sans, fontWeight: 300, fontSize: 17 })}
                    />
                    {showDropdown && (
                      <div className="lx-fade" style={{ ...dropdownBox, maxHeight: 240 }}>
                        {matches.length === 0 ? (
                          <div style={{ ...dropdownRow, cursor: 'default', color: MUTE, fontStyle: 'italic', borderBottom: 0 }}>
                            {language === 'zh' ? '列表里没有，直接输入即可' : 'Not in the list — just type it'}
                          </div>
                        ) : (
                          matches.map(ct => (
                            <div
                              key={ct}
                              className="lx-reg-opt"
                              // onMouseDown so we set the value before the input
                              // blurs (which would close the popup first).
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setFormData({ ...formData, city: ct });
                                setCityFocused(false);
                              }}
                              style={dropdownRow}
                            >
                              {translateLocation(ct, language)}
                              {language === 'zh' && translateLocation(ct, 'zh') !== ct && (
                                <span style={{ marginLeft: 10, fontSize: 13, color: MUTE }}>{ct}</span>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {errors.location && <div style={{ marginTop: -14 }}>{errLine(errors.location)}</div>}
          </div>
        );

      case 'textarea':
        return (
          <div style={{ position: 'relative' }}>
            <textarea
              ref={inputRef}
              name={currentStepData.field}
              className="lx-input"
              value={formData[currentStepData.field]}
              onChange={handleChange}
              placeholder={currentStepData.placeholder}
              rows={5}
              style={{
                fontSize: 15, fontWeight: 300, lineHeight: 1.75, padding: '16px 18px',
                background: L.card, border: `1px solid ${L.line}`, borderRadius: L.radius,
                resize: 'vertical', minHeight: 150, fontFamily: L.sans,
              }}
            />
            {/* AI-generate button — only shown on the bio step. Uses
                everything the user has filled so far (name / birthday /
                zodiac / occupation / location / current bio draft) to
                produce a vivid, empathic self-introduction. */}
            <div style={{ marginTop: 14, display: 'flex', gap: 14, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              {currentStepData.field === 'bio' ? (
                <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={handleGenerateBio}
                    disabled={generatingBio}
                    className="lx-btn-ghost"
                    style={{
                      ...btn.ghost, padding: '10px 18px', fontSize: 12.5,
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      cursor: generatingBio ? 'wait' : 'pointer',
                      opacity: generatingBio ? 0.55 : 1,
                    }}
                  >
                    <Sparkles size={14} strokeWidth={1.25} color={generatingBio ? undefined : L.gold} />
                    {generatingBio ? tr('AI 生成中...', 'Generating...') : tr('AI 生成自我介绍', 'Generate with AI')}
                  </button>
                  <span style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 15, color: MUTE }}>
                    {tr('基于您前面填写的信息生成', 'Based on the info you filled in above')}
                  </span>
                </div>
              ) : <span />}
              <span style={kicker({ fontSize: 12 })}>
                {formData[currentStepData.field]?.length || 0} {tr('字', 'characters')}
              </span>
            </div>
          </div>
        );

      case 'date':
        return (
          <div>
            <DatePickerInput
              ref={inputRef}
              name={currentStepData.field}
              value={formData[currentStepData.field]}
              onChange={handleChange}
              max={new Date().toISOString().split('T')[0]}
              style={{ display: 'block', width: '100%' }}
              inputStyle={pickerInputStyle(!!fieldErr)}
            />
            {formData.birthday && (
              <div className="lx-fade-up" style={{
                marginTop: 26, display: 'flex', alignItems: 'stretch',
                borderTop: `1px solid ${L.line}`, borderBottom: `1px solid ${L.line}`,
              }}>
                {age && (
                  <div style={{ flex: 1, padding: '18px 0', textAlign: 'center' }}>
                    <div style={display(40)}>{age}</div>
                    <div style={kicker({ fontSize: 12, marginTop: 8 })}>{tr('岁', 'Years old')}</div>
                  </div>
                )}
                {age && zodiac && <div style={{ width: 1, background: L.line }} />}
                {zodiac && (
                  <div style={{ flex: 1, padding: '18px 0', textAlign: 'center' }}>
                    <div style={display(40, { color: L.goldDeep })}>{zodiac.symbol}</div>
                    <div style={kicker({ fontSize: 12, marginTop: 8 })}>{zodiac.sign}</div>
                  </div>
                )}
              </div>
            )}
            {errLine(fieldErr)}
          </div>
        );

      case 'time':
        return (
          <div style={{ position: 'relative' }}>
            <TimePickerInput
              ref={inputRef}
              name={currentStepData.field}
              value={formData[currentStepData.field]}
              onChange={handleChange}
              style={{ display: 'block', width: '100%' }}
              inputStyle={pickerInputStyle(false)}
            />
            <div style={{ marginTop: 16, fontFamily: L.serif, fontStyle: 'italic', fontSize: 16, color: MUTE }}>
              {tr('不知道？可以跳过此步', "Don't know? You can skip this step")}
            </div>
          </div>
        );

      case 'occupation': {
        const occ = String(formData.occupation || '').trim();
        const q = occ.toLowerCase();
        const matches = q
          ? occupationOptions.filter(o => o.toLowerCase().includes(q))
          : occupationOptions;
        const showDropdown = occupationFocused
          && !(matches.length === 1 && matches[0].toLowerCase() === q);
        return (
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'relative' }}>
              <input
                ref={inputRef}
                name="occupation"
                className="lx-input"
                value={formData.occupation}
                onChange={handleChange}
                onFocus={() => setOccupationFocused(true)}
                onBlur={() => setTimeout(() => setOccupationFocused(false), 150)}
                placeholder={currentStepData.placeholder}
                autoComplete="off"
                style={bigInput(!!fieldErr)}
              />
              {showDropdown && (
                <div className="lx-fade" style={{ ...dropdownBox, maxHeight: 260 }}>
                  {matches.length === 0 ? (
                    <div style={{ ...dropdownRow, cursor: 'default', color: MUTE, fontStyle: 'italic', borderBottom: 0 }}>
                      {tr('没找到，直接输入即可', 'Not in the list — just type it')}
                    </div>
                  ) : (
                    matches.slice(0, 30).map(opt => (
                      <div
                        key={opt}
                        className="lx-reg-opt"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setFormData({ ...formData, occupation: opt });
                          setOccupationFocused(false);
                        }}
                        style={dropdownRow}
                      >
                        {opt}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            {errLine(fieldErr)}
          </div>
        );
      }

      case 'checkbox': {
        const summary = [
          { k: tr('姓名', 'Name'),       v: `${formData.firstName} ${formData.lastName}` },
          { k: tr('邮箱', 'Email'),      v: formData.email },
          { k: tr('年龄', 'Age'),        v: `${age}${tr(' 岁', ' years old')}` },
          { k: tr('星座', 'Star Sign'),  v: `${zodiac?.symbol || ''} ${zodiac?.sign || ''}` },
          { k: tr('性别', 'Gender'),     v: genderOptions.find(g => g.value === formData.gender)?.label },
          { k: tr('居住地', 'Location'), v: [formData.city, formData.state, countries.find(c => c.code === formData.country)?.name].filter(Boolean).join(', ') },
        ];
        return (
          <div>
            {/* Referral Code Display */}
            {formData.referral_code && (
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 22,
                paddingBottom: 14, borderBottom: `1px solid ${L.line}`,
              }}>
                <span style={kicker({ fontSize: 12, color: L.goldDeep })}>{tr('推荐码', 'Referral Code')}</span>
                <span style={{ fontFamily: L.serif, fontSize: 20, color: L.ink, letterSpacing: '.06em' }}>{formData.referral_code}</span>
              </div>
            )}

            {/* Summary */}
            <div style={{
              display: 'grid', gridTemplateColumns: isDesktop ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
              borderTop: `1px solid ${L.ink}`, marginBottom: 30,
            }}>
              {summary.map((r, i) => (
                <div key={r.k} style={{
                  padding: '14px 0', borderBottom: `1px solid ${L.line}`,
                  paddingLeft: isDesktop && i % 2 === 1 ? 20 : 0,
                  borderLeft: isDesktop && i % 2 === 1 ? `1px solid ${L.line}` : 'none',
                }}>
                  <div style={kicker({ fontSize: 12, marginBottom: 6 })}>{r.k}</div>
                  <div style={{ fontFamily: L.serif, fontSize: 19, color: L.ink, lineHeight: 1.3, wordBreak: 'break-word' }}>{r.v}</div>
                </div>
              ))}
            </div>

            {/* Terms Checkbox — opens the signature modal; you can only
                check it by actually signing the agreement in the modal. */}
            <div
              role="checkbox"
              aria-checked={!!formData.agreedToTerms}
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); e.currentTarget.click(); } }}
              onClick={() => {
                if (!formData.agreedToTerms) {
                  setShowSignatureModal(true);
                } else {
                  // Clicking an already-checked box clears the signature.
                  setFormData({ ...formData, agreedToTerms: false });
                  setSignatureData(null);
                  setSignatureStyle('');
                }
              }}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 14, cursor: 'pointer',
                padding: '18px 20px', borderRadius: L.radius,
                background: formData.agreedToTerms ? L.card : 'transparent',
                border: `1px solid ${formData.agreedToTerms ? L.ink : (errors.agreedToTerms ? L.danger : L.line)}`,
                transition: `all .35s ${L.ease}`,
              }}
            >
              <div style={{
                width: 20, height: 20, marginTop: 1, borderRadius: L.radius,
                border: `1px solid ${L.ink}`,
                background: formData.agreedToTerms ? L.ink : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition: `all .3s ${L.ease}`,
              }}>
                {formData.agreedToTerms && <Check size={13} strokeWidth={2} color={L.paper} />}
              </div>
              <span style={{ fontFamily: L.sans, fontSize: 14.5, color: L.ink2, lineHeight: 1.6, fontWeight: 300 }}>
                {language === 'zh' ? (
                  <>
                    我已阅读并同意签署
                    <span style={{ color: L.ink, fontWeight: 500 }}>《服务条款》</span>
                    与
                    <span style={{ color: L.ink, fontWeight: 500 }}>《隐私政策》</span>
                  </>
                ) : (
                  <>
                    I have read and agree to sign the{' '}
                    <span style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 17, color: L.ink }}>Terms of Service</span>
                    {' '}and{' '}
                    <span style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 17, color: L.ink }}>Privacy Policy</span>
                  </>
                )}
              </span>
            </div>

            {/* Signature preview (after signing) */}
            {formData.agreedToTerms && signatureData && (
              <div className="lx-fade-up" style={{
                marginTop: 18, padding: '22px 24px', background: L.card,
                border: `1px solid ${L.line}`, borderRadius: L.radius, textAlign: 'center',
              }}>
                <div style={kicker({ fontSize: 12, color: L.goldDeep, marginBottom: 10 })}>
                  {language === 'zh' ? '电子签名' : 'Signed as'}
                </div>
                <div style={{ fontFamily: signatureData.fontFamily, fontSize: 32, color: L.ink, lineHeight: 1.25 }}>
                  {signatureData.name}
                </div>
                <div style={{ width: 120, height: 1, background: L.line, margin: '12px auto 10px' }} />
                <div style={{ fontFamily: L.sans, fontSize: 13, color: MUTE }}>
                  {new Date(signatureData.signedAt).toLocaleString()}
                </div>
              </div>
            )}

            {errLine(errors.agreedToTerms)}
          </div>
        );
      }

      default:
        return (
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'relative' }}>
              <input
                ref={inputRef}
                type={currentStepData.type === 'password' && showPassword ? 'text' : currentStepData.type}
                name={currentStepData.field}
                className="lx-input"
                value={formData[currentStepData.field]}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder={currentStepData.placeholder}
                autoComplete={currentStepData.type === 'password' ? 'new-password' : 'off'}
                style={bigInput(!!fieldErr, { paddingRight: currentStepData.type === 'password' ? 44 : 0 })}
              />
              {currentStepData.type === 'password' && (
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? tr('隐藏密码', 'Hide password') : tr('显示密码', 'Show password')}
                  style={{
                    position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: MUTE, padding: 8,
                  }}
                >
                  {showPassword ? <EyeOff size={18} strokeWidth={1.25} /> : <Eye size={18} strokeWidth={1.25} />}
                </button>
              )}
            </div>
            {errLine(fieldErr)}
          </div>
        );
    }
  };

  const StepIcon = currentStepData.icon;
  const isLast = currentStep === steps.length - 1;
  const stepCounter = `${pad2(currentStep + 1)} / ${pad2(steps.length)}`;

  // Language toggle + sign-in link, shared by both breakpoints.
  const langToggle = (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      {[
        { code: 'zh', label: '中文' },
        { code: 'en', label: 'EN' },
      ].map((opt, i) => {
        const active = language === opt.code;
        return (
          <React.Fragment key={opt.code}>
            {i > 0 && <span style={{ width: 1, height: 11, background: L.line }} />}
            <button
              type="button"
              onClick={() => switchLanguage(opt.code)}
              className={`lx-link${active ? ' lx-active' : ''}`}
              aria-pressed={active}
              style={{
                ...kicker({ fontSize: 12.5, color: active ? L.ink : MUTE, letterSpacing: '.18em' }),
                background: 'none', borderTop: 0, borderLeft: 0, borderRight: 0, padding: '6px 0',
                borderBottom: `1px solid ${active ? L.ink : 'transparent'}`,
              }}
            >
              {opt.label}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
  const loginLink = (
    <Link
      to="/login"
      className="lx-link"
      style={{ ...kicker({ fontSize: 12.5, color: L.goldDeep, letterSpacing: '.12em' }), textDecoration: 'none', whiteSpace: 'nowrap' }}
    >
      {language === 'zh' ? '已有账号？' : 'Already have an account?'}
    </Link>
  );
  const utilityCluster = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
      {langToggle}
      {loginLink}
    </div>
  );

  const wordmark = (size) => (
    <div style={{
      fontFamily: L.serif, fontSize: size, fontWeight: 500, letterSpacing: '.32em',
      lineHeight: 1, color: L.ink, marginRight: '-.32em',
    }}>FINDU</div>
  );

  const pageCss = `
    @keyframes lxRegNext { from { opacity:0; transform:translateX(24px); } to { opacity:1; transform:none; } }
    @keyframes lxRegPrev { from { opacity:0; transform:translateX(-24px); } to { opacity:1; transform:none; } }
    .lx-reg-next { animation:lxRegNext .65s ${L.easeOut} backwards; }
    .lx-reg-prev { animation:lxRegPrev .65s ${L.easeOut} backwards; }
    .lx-reg select.lx-input { appearance:none; -webkit-appearance:none; -moz-appearance:none; cursor:pointer; padding-right:28px !important; }
    .lx-reg select option { color:${L.ink}; }
    .lx-reg .lx-chip { border-radius:2px; }
    .lx-reg .lx-input::placeholder { color:${MUTE}; }
    .lx-reg-opt { transition:background .2s ${L.ease}; }
    .lx-reg-opt:hover { background:${L.paper2}; }
    .lx-reg .lx-btn:disabled:hover { background:${L.ink} !important; color:${L.paper} !important; }
    .lx-reg .lx-btn-ghost:disabled:hover { background:transparent !important; color:${L.ink} !important; }
    .lx-reg-sig { transition:border-color .3s ${L.ease}, background .3s ${L.ease}; }
    .lx-reg-sig:hover { border-color:${L.ink} !important; }
    .lx-reg [role="checkbox"]:focus-visible, .lx-reg .lx-chip:focus-visible { outline:1px solid ${L.gold}; outline-offset:3px; }
    @media (prefers-reduced-motion: reduce) { .lx-reg-next, .lx-reg-prev { animation:none !important; } }
  `;

  // Agreement heading styles (signature modal).
  const agH4 = { fontFamily: L.serif, fontSize: 18, fontWeight: 600, color: L.ink, margin: '22px 0 8px', letterSpacing: '.01em' };

  return (
    <div className="lx-root lx-reg" style={{
      minHeight: '100vh',
      background: L.paper,
      display: isDesktop ? 'grid' : 'block',
      gridTemplateColumns: isDesktop ? 'minmax(340px, 38%) minmax(0, 1fr)' : undefined,
      position: 'relative',
    }}>
      <LuxeStyles />
      <style>{pageCss}</style>

      {isDesktop ? (
        /* ── Desktop: sticky editorial panel ─────────────────────────── */
        <aside className="lx-noscroll" style={{
          position: 'sticky', top: 0, height: '100vh', overflowY: 'auto', boxSizing: 'border-box',
          background: L.paper2, borderRight: `1px solid ${L.line}`,
          padding: '56px 56px 44px', display: 'flex', flexDirection: 'column',
        }}>
          <div className="lx-fade-up">{wordmark(60)}</div>
          <div className="lx-fade-up lx-d1" style={kicker({ color: L.goldDeep, marginTop: 18, fontSize: 12 })}>
            Maison of Love · 高端婚恋机构
          </div>
          <div className="lx-rule lx-rule-draw" style={{ margin: '30px 0 26px', background: L.ink, width: 48 }} />
          <div className="lx-fade-up lx-d2" style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 30, lineHeight: 1.15, color: L.ink }}>
            {tr('会员申请', 'Membership by application')}
          </div>
          <div className="lx-fade-up lx-d2" style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 17, color: MUTE, marginTop: 6 }}>
            {tr('Membership by application', '会员申请')}
          </div>

          <div className="lx-fade-up lx-d3" style={{ marginTop: 40 }}>
            <div style={kicker({ fontSize: 12, marginBottom: 12 })}>{tr('目录', 'Contents')}</div>
            <ol style={{ listStyle: 'none', margin: 0, padding: 0, borderTop: `1px solid ${L.line}` }}>
              {steps.map((s, i) => {
                const current = i === currentStep;
                const done = i < currentStep;
                return (
                  <li key={s.id} aria-current={current ? 'step' : undefined} style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '7px 0', borderBottom: `1px solid ${L.lineSoft}`,
                    color: current ? L.ink : done ? L.ink2 : MUTE,
                    transition: `color .4s ${L.ease}`,
                  }}>
                    <span style={{
                      fontFamily: L.serif, fontSize: 16, width: 22, fontVariantNumeric: 'tabular-nums',
                      color: current ? L.ink : done ? L.ink2 : MUTE,
                    }}>{pad2(i + 1)}</span>
                    <span style={{
                      flex: 1, fontFamily: L.sans, fontSize: 13.5, letterSpacing: '.04em',
                      fontWeight: current ? 500 : 300,
                    }}>
                      {labelOf(s)}
                      {s.optional && (
                        <span style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 14, color: MUTE, marginLeft: 8 }}>
                          {tr('选填', 'optional')}
                        </span>
                      )}
                    </span>
                    {current && <span style={{ width: 22, height: 1, background: L.ink }} />}
                    {done && <Check size={13} strokeWidth={1.25} color={L.gold} />}
                  </li>
                );
              })}
            </ol>
          </div>

          <div style={{ marginTop: 'auto', paddingTop: 32 }}>
            <div style={{ height: 1, background: L.line, position: 'relative', overflow: 'hidden' }}>
              <div style={{
                position: 'absolute', left: 0, top: 0, bottom: 0, width: `${progress}%`,
                background: L.goldDeep, transition: `width .7s ${L.easeOut}`,
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              <span style={kicker({ fontSize: 12 })}>{tr('进度', 'Progress')}</span>
              <span style={kicker({ fontSize: 12, color: L.ink })}>{stepCounter}</span>
            </div>
          </div>
        </aside>
      ) : (
        /* ── Mobile: masthead + thin progress line ───────────────────── */
        <header style={{ padding: '22px 16px 0' }}>
          <div className="lx-fade" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            {wordmark(28)}
            {langToggle}
          </div>
          <div className="lx-fade lx-d1" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
            <span style={kicker({ fontSize: 12, color: L.goldDeep })}>Maison of Love · 高端婚恋机构</span>
            {loginLink}
          </div>
          <div style={{ height: 1, background: L.line, position: 'relative', overflow: 'hidden', marginTop: 20 }}>
            <div style={{
              position: 'absolute', left: 0, top: 0, bottom: 0, width: `${progress}%`,
              background: L.ink, transition: `width .7s ${L.easeOut}`,
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, gap: 12 }}>
            <span style={kicker({ fontSize: 12, color: L.ink })}>{tr('步骤', 'Step')} {stepCounter}</span>
            <span style={kicker({ fontSize: 12 })}>{labelOf(currentStepData)}</span>
          </div>
        </header>
      )}

      {/* ── Form column ─────────────────────────────────────────────────── */}
      <main style={{ display: 'flex', flexDirection: 'column', minHeight: isDesktop ? '100vh' : undefined, minWidth: 0 }}>
        {isDesktop && (
          <div className="lx-fade" style={{ display: 'flex', justifyContent: 'flex-end', padding: '30px 56px 0' }}>
            {utilityCluster}
          </div>
        )}

        <div style={{
          flex: 1, display: 'flex', alignItems: isDesktop ? 'center' : 'flex-start', justifyContent: 'center',
          padding: isDesktop ? '40px 56px 72px' : '36px 16px 56px',
        }}>
          {/* Question Card */}
          <div
            key={currentStep}
            className={direction === 'next' ? 'lx-reg-next' : 'lx-reg-prev'}
            style={{ width: '100%', maxWidth: 560 }}
          >
            {/* Numbered kicker */}
            <div className="lx-fade lx-d1" style={{
              display: 'flex', alignItems: 'center', gap: 16,
              borderBottom: `1px solid ${L.ink}`, paddingBottom: 14,
            }}>
              <span style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 20, color: L.goldDeep, whiteSpace: 'nowrap' }}>
                No. {pad2(currentStep + 1)}
              </span>
              <span style={kicker({ fontSize: 12, letterSpacing: '.26em', minWidth: 0 })}>{labelOf(currentStepData)}</span>
              {StepIcon && <StepIcon size={14} strokeWidth={1.25} color={MUTE} style={{ marginLeft: 'auto' }} />}
            </div>

            {/* Question */}
            <h1 className="lx-fade-up lx-d1" style={display(isDesktop ? 52 : 36, { marginTop: 22 })}>
              {currentStepData.question}
            </h1>

            <p className="lx-fade-up lx-d2" style={{
              fontFamily: L.serif, fontStyle: 'italic', fontSize: isDesktop ? 20 : 18,
              color: L.ink2, margin: '12px 0 0', lineHeight: 1.4,
            }}>
              {currentStepData.subtitle}
            </p>

            <div style={{ height: isDesktop ? 40 : 30 }} />

            {/* Input Field */}
            <div className="lx-fade-up lx-d3">
              {!['checkbox', 'location', 'textarea'].includes(currentStepData.type) && fieldKicker(
                <>
                  {labelOf(currentStepData)}
                  {currentStepData.optional && (
                    <span style={{ marginLeft: 10, color: L.goldDeep }}>· {tr('选填', 'Optional')}</span>
                  )}
                </>,
                { marginBottom: currentStepData.type === 'select' ? 14 : 2 }
              )}
              {renderInputField()}
            </div>

            {/* Action Buttons */}
            <div className="lx-fade-up lx-d4" style={{
              marginTop: 44, display: 'flex', gap: 12,
              justifyContent: currentStep > 0 ? 'space-between' : 'flex-end',
            }}>
              {currentStep > 0 && (
                <button
                  type="button"
                  onClick={handlePrev}
                  className="lx-btn-ghost"
                  style={{
                    ...btn.ghost, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    height: 44, fontSize: 12.5, letterSpacing: '.22em',
                    padding: isDesktop ? '0 24px' : '0 18px',
                    flex: isDesktop ? '0 0 auto' : '0 0 auto',
                  }}
                >
                  <ArrowLeft size={15} strokeWidth={1.5} />
                  {tr('上一步', 'Back')}
                </button>
              )}

              {!isLast ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="lx-btn"
                  style={{
                    ...btn.primary, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                    height: 44, fontSize: 12.5, letterSpacing: '.22em',
                    padding: '0 30px', flex: isDesktop ? '0 0 auto' : 1,
                  }}
                >
                  {currentStepData.optional ? tr('跳过', 'Skip') : tr('继续', 'Continue')}
                  <ArrowRight size={15} strokeWidth={1.5} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="lx-btn"
                  style={{
                    ...btn.primary, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 12,
                    height: 44, fontSize: 12.5, letterSpacing: '.22em',
                    padding: '0 30px', flex: isDesktop ? '0 0 auto' : 1,
                    opacity: loading ? 0.55 : 1, cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loading ? tr('创建中...', 'Creating Account...') : (
                    <>
                      {tr('创建账号', 'Create Account')}
                      <Check size={15} strokeWidth={1.5} />
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Keyboard Hint */}
            {isDesktop && !['checkbox', 'textarea', 'location', 'select'].includes(currentStepData.type) && (
              <div className="lx-fade lx-d5" style={{
                marginTop: 26, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8,
                ...kicker({ fontSize: 12, letterSpacing: '.14em' }),
              }}>
                {tr('按 ', 'Press ')}<kbd style={{
                  border: '1px solid rgba(17,17,17,.25)', borderRadius: L.radius, padding: '2px 8px',
                  fontFamily: L.sans, fontSize: 12, color: L.ink, background: L.card, letterSpacing: '.06em',
                }}>Enter ↵</kbd>{tr(' 继续', ' to continue')}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Signature modal — clicking the terms checkbox opens this. The user
          reads the agreement and picks a signature style; submitting the
          modal sets agreedToTerms=true (and stores the signature). Closing
          without signing leaves agreedToTerms=false, which the create-account
          step validation blocks on. Portalled to <body> so no animated
          ancestor can trap the fixed overlay. */}
      {showSignatureModal && createPortal(
        <div className="lx-root lx-reg" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(17,17,17,.55)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          padding: isDesktop ? 24 : 12, backdropFilter: 'blur(3px)',
        }}>
          <div className="lx-fade-up" style={{
            background: L.card, borderRadius: L.radius, width: '100%', maxWidth: 600,
            maxHeight: '90vh', overflow: 'auto', boxShadow: 'none',
            border: `1px solid ${L.line}`,
          }}>
            {/* Header */}
            <div style={{
              padding: isDesktop ? '28px 32px 20px' : '22px 20px 16px', borderBottom: `1px solid ${L.line}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16,
            }}>
              <div>
                <div style={kicker({ fontSize: 12, color: L.goldDeep, marginBottom: 10 })}>
                  {tr('服务协议', 'Service Agreement')} · FINDU
                </div>
                <h2 style={display(isDesktop ? 32 : 26)}>
                  {language === 'zh' ? '签署服务条款' : 'Sign Terms of Service'}
                </h2>
                <p style={{ fontFamily: L.serif, fontStyle: 'italic', fontSize: 16, color: MUTE, margin: '6px 0 0' }}>
                  {language === 'zh' ? 'Sign Terms of Service' : '签署服务条款'}
                </p>
              </div>
              <button
                type="button"
                aria-label={tr('关闭', 'Close')}
                onClick={() => setShowSignatureModal(false)}
                className="lx-icon-btn"
                style={{ ...btn.icon, flexShrink: 0 }}
              ><X size={16} strokeWidth={1.25} /></button>
            </div>

            {/* Body */}
            <div style={{ padding: isDesktop ? '24px 32px' : '20px' }}>
              {/* Full Service Agreement — bilingual, scrollable. */}
              <div style={{
                background: L.paper, border: `1px solid ${L.line}`, borderRadius: L.radius,
                padding: isDesktop ? '20px 22px' : '16px', marginBottom: 18,
                fontFamily: L.sans, fontSize: 13, fontWeight: 400, color: L.ink2, lineHeight: 1.75,
                maxHeight: 320, overflowY: 'auto',
              }}>
                <h3 style={{ margin: '0 0 10px', fontFamily: L.serif, fontSize: 22, fontWeight: 500, color: L.ink }}>Service Agreement / 服务协议</h3>
                <p><strong>WHEREAS</strong>, FIND-U GROUP LTD, a professional matchmaking agency in Los Angeles ("Company"), is in the business of providing services to individuals to facilitate the Client meeting other people for the purpose of entering into a serious long-term romantic relationship;<br/>鉴于，FIND-U GROUP LTD 是一家位于洛杉矶的专业婚恋服务机构（以下简称"公司"），业务范围是为个人提供服务，帮助客户与他人见面，以便建立长期认真的恋爱关系；</p>
                <p><strong>WHEREAS</strong>, Client desires to engage and receive services offered by the Company;<br/>鉴于，客户希望接受公司提供的服务；</p>
                <p><strong>THEREFORE</strong>, Company desires to provide services to Client all pursuant to the rates, limitations, terms, and conditions of the Agreement.<br/>因此，公司希望根据本协议的费率、限制、条款和条件向客户提供服务。</p>
                <p><strong>NOW, THEREFORE</strong>, the parties agree as follows: 现因此，双方在此确认如下协议：</p>

                <h4 style={agH4}>Section 1. Definitions / 定义</h4>
                <p>1.1 <strong>"Fee"</strong> means monies paid by Client to Company in anticipation of Services being provided for the Term to Client by Company and is specified in Section 3 in the Agreement.<br/>"费用"是指客户支付给公司的款项，以便公司在协议期内向客户提供服务，并在本协议第 3 条中列明。</p>
                <p>1.2 <strong>"Services"</strong> means professional matchmaking services through both online and offline channels which are differentiated by service packages selected by Client.<br/>"服务"是指通过线上和线下渠道提供的专业婚恋服务，具体由客户选择的服务套餐决定。</p>
                <p>1.3 <strong>"Term"</strong> means the period specified in the service package selected by the Client.<br/>"期限"是指客户选择的服务包中指定的期限。</p>

                <h4 style={agH4}>Section 2. Scope of Services / 服务范围</h4>
                <p>2.1 This Service Agreement ("Agreement") is made between FIND-U GROUP LTD, a professional matchmaking agency in Los Angeles ("Company"), and ("Client"). The Company offers matchmaking services through both online and offline channels.<br/>本服务协议（"协议"）由位于洛杉矶的专业婚介公司 FIND-U GROUP LTD（"公司"）与（"客户"）签订。公司通过线上和线下渠道提供婚介服务。</p>

                <h4 style={agH4}>Section 3. Fees / 费用</h4>
                <p>3.1 The Fees are due on Effective Date. Company will not be obligated to perform Services until Fees are received and processed.<br/>费用在生效日期支付。公司在收到并处理费用之前没有义务提供服务。</p>
                <p>3.2 The Fee paid by the Client shall only be applicable to pay for the Services, and under no circumstance will the Fee cover any expenses that the Client may encounter during the course of Services. This includes any costs for services with any third-party vendors, costs of any activities participated in by Client on dates, including but not limited to admission costs, costs for food/beverages, costs for personal hygiene, clothes, and any other associated cost for any dates.<br/>客户支付的费用仅适用于支付服务费用，在任何情况下，这些费用都不会涵盖客户在服务过程中可能遇到的任何费用。这包括与任何第三方供应商的服务费用，客户在约会中参与的任何活动的费用，包括但不限于入场费用、食品/饮料费用、个人卫生费用、衣服费用以及任何其他相关的约会费用。</p>
                <p><strong>Payment 付款方式：</strong> Balance has to pay in full to start the service. 服务开始之前一次性付款。</p>

                <h4 style={agH4}>Section 4. Termination and Refund / 终止和退款</h4>
                <p><strong>Start of the service / 服务开始：</strong> The service starts when the contract is signed and the payment is made. 签约付费后，服务开始。</p>
                <p><strong>Termination of the service / 服务终止：</strong> The termination is when the customer proposes to terminate the matching within the expiration of the service period, or the expiration date is the termination time. If termination time arrives but the number of people promised by the company is not reached, the termination time will be automatically renewed. If the members are not satisfied with the recommendation, the company can extend the service for up to half of the service period.<br/>服务终止是在客人在服务期到期之内，提出终止继续匹配为止；或者以到期日为合同终止时间。如果到期，但是公司承诺的人数没有达到，则自动续期。如果会员对我们推荐的成员都不满意，公司最长可延期服务期的一半的服务时间。</p>
                <p>However, if there is no feedback from the two parties after the recommendation, the system defaults to a suitable match after two months and will no longer recommend anyone. If the service expires, the member automatically becomes a certified member and can enjoy the right to be selected permanently. If the member still wants to continue using the package, he/she can enjoy a 20% discount on the purchase.<br/>然而，如果推荐之后，两方交往超过两个月没有反馈，系统默认为匹配合适，将不再继续推荐。如果服务到期了，会员自动变为被认证会员，可享受永久被选择的权利。如果会员还想继续使用该套餐，可享受购买 20% 优惠。</p>
                <p><strong>Refund Terms 退款条件：</strong> No refunds will be given once the contract is signed. 一旦签约，不予退费。</p>

                <h4 style={agH4}>Section 5. Representations and Warranties / 陈述与保证</h4>
                <p><strong>5.1 Company Representations and Warranties / 公司陈述与保证：</strong></p>
                <p>a. There are no known legal barriers to Company's ability to provide Services to Client upon Effective Date; 在生效日期，公司没有已知的法律障碍阻止其向客户提供服务；</p>
                <p>b. All Services will be performed in a good and workmanlike manner; 所有服务都将以良好和专业的方式进行；</p>
                <p>c. It has the authority to enter into this Agreement; 公司有权签订本协议；</p>
                <p>d. It will comply with all applicable laws in the performance of the Services. 公司将在提供服务时遵守所有适用的法律。</p>
                <p><strong>5.2 Client Representations and Warranties / 客户陈述与保证：</strong></p>
                <p>a. Has the authority and capacity to enter into this Agreement; 有权力和能力签订本协议；</p>
                <p>b. Is at least eighteen (18) years of age; 至少十八（18）岁；</p>
                <p>c. Has provided accurate information to Company in response to Company's inquiries regarding the Client's background and current marital status; 已向公司提供了准确的信息，以回应公司关于客户背景和当前婚姻状况的询问；</p>
                <p>d. Will allow Company to conduct reasonable investigations with respect to any background and status information provided by the Client (optional); 将允许公司对客户提供的任何背景和状态信息进行合理调查（可选）；</p>
                <p>e. Will make himself/herself reasonably available to meet with the Company as required by Services purchased by the Client; 将合理安排时间与公司见面，按照客户购买的服务要求；</p>
                <p>f. Will cooperate fully with Company throughout the Term, such that the Client may receive the benefit of Company's Services; 在整个服务期间将与公司充分合作，以便客户能从公司的服务中受益；</p>
                <p>g. Is not addicted to drugs, alcohol, prescriptions, or sex; 不沉迷于毒品、酒精、处方药或性行为；</p>
                <p>h. Is not engaged in behaviors that are, or could be deemed as, verbally, physically, sexually, or emotionally abusive; 没有从事或可能被视为言语、身体、性或情感虐待的行为；</p>
                <p>i. Is solely responsible for his/her conduct and for the information he/she provides to Company; 对其行为和向公司提供的信息负全责；</p>
                <p>j. Shall conduct himself/herself at all times during any meetings with Company employees, outside vendors or contractors of Company or any individuals that Company provides Introductions to in a dignified and respectful manner; 在与公司员工、外部供应商或承包商或公司介绍的任何个人的任何会议期间，应始终以有尊严和尊重的方式行事；</p>
                <p>k. Shall treat any individual with whom an Introduction is arranged by Company with the utmost respect, including, without limitation, abiding by all applicable state and Federal laws, and all customary conduct expected of a lady or gentleman; 应以最大尊重对待公司安排介绍的任何个人，包括但不限于遵守所有适用的州和联邦法律，以及所有期望的绅士或女士的惯例行为；</p>
                <p>l. Will not use the Services in a manner inconsistent with any and all applicable state or Federal laws and regulations. 不会以任何与所有适用的州或联邦法律法规不一致的方式使用服务。</p>

                <h4 style={agH4}>Section 6. Disclaimers / 免责声明</h4>
                <p>6.1 Company makes no representation or warranty, either implied or express, concerning Client's experience or outcome with the Services.<br/>公司不对客户使用服务的体验或结果作出任何明示或暗示的陈述或保证。</p>
                <p>6.2 Client acknowledges that Client shall not rely on any investigation by Company or other due diligence by Company into the background of any person to whom Company may wish to introduce to Client ("Introduction Candidate"). Company specifically disclaims and does not represent, warrant, or guarantee the accuracy of any statement made by or on behalf of any Introduction Candidate, nor shall the Company be liable for any inaccuracy in any description of an Introduction Candidate made by Company unless Company had actual knowledge of such inaccuracy.<br/>客户承认，客户不应依赖公司对任何公司可能希望介绍给客户的人的背景进行的任何调查或其他尽职调查（"介绍候选人"）。公司特别声明不对任何介绍候选人或代表介绍候选人所作的任何陈述的准确性作出陈述、保证或担保，除非公司实际知道该陈述的不准确性，否则公司不对任何不准确描述承担责任。</p>
                <p>6.3 Client releases Company, its employees, officers, members, managers, agents, vendors, representatives, and independent contractors, from any and all claims, actions, causes of action, loss, and liability resulting from any and all Introductions, regardless of the reasons therefore, including claims for personal injury.<br/>客户解除公司、其员工、管理人员、成员、经理、代理商、供应商、代表和独立承包商因任何和所有介绍而引起的任何和所有索赔、诉讼、诉因、损失和责任，无论其原因如何，包括人身伤害索赔。</p>
                <p>6.4 Client understands that coaching and consulting provided by Company is for education, to help with goal setting, and to act as an accountability program, and that coaching and consulting are not mental health counseling nor treatment for mental health issues.<br/>客户理解，公司提供的辅导和咨询是用于教育、帮助设定目标和作为问责计划，辅导和咨询不是心理健康咨询，也不是心理健康问题的治疗。</p>

                <h4 style={agH4}>Section 7. Privacy and Confidentiality / 隐私与保密</h4>
                <p>7.1 <strong>Privacy and Confidentiality by Company.</strong> Company shall respect the Client's right to privacy and will not share any private information from the Client to others who are not contracted with the Company unless it is essential to providing the Services. Without limiting the foregoing, Company may disclose confidential information when appropriate. Company shall take reasonable measures to protect the confidentiality of all information obtained in the course of providing the Services. Company will inform the Client, to the extent possible, about the disclosure of confidential information. Client affirmatively agrees that any information shared by Client with an Introduction Client is not controllable by Company, and dissemination from the Introduction Client to friends, family or other contacts of Introduction Client is to be reasonably expected.<br/>公司应尊重客户的隐私权，不会向与公司无合同关系的其他人分享客户的任何私人信息，除非这是提供服务所必需的。在不限制上述内容的情况下，公司可在适当时披露机密信息。公司应采取合理措施保护在提供服务过程中获取的所有信息的机密性。公司将尽可能地通知客户有关机密信息的披露。客户明确同意客户与介绍客户共享的任何信息不受公司控制，介绍客户向朋友、家人或其他联系人的传播是合理预期的。</p>
                <p>7.2 <strong>Privacy and Confidentiality by Client.</strong> Client agrees that all information furnished to him/her, to which Client has access during the performance of Services, or to which Client has access under this Agreement, shall be kept confidential and shall remain the proprietary information and exclusive property of Company. Client understands that any information Client shares with friends, family, and acquaintances is not controllable by Company, and is reasonably expected to be disseminated.<br/>客户同意，所有向其提供的信息，在服务执行过程中客户有权访问的信息，或客户根据本协议有权访问的信息，均应保密并保持为公司的专有信息和独家财产。客户理解，客户与朋友、家人和熟人共享的任何信息不受公司控制，并且合理预期会被传播。</p>

                <h4 style={agH4}>Section 8. Indemnity / 赔偿</h4>
                <p>8.1 <strong>Indemnification by Client.</strong> Client shall defend, indemnify and hold harmless Company, its affiliates, employees, directors, officers, and agents from and against any and all expenses, costs, damages, losses, liabilities, and judgments, including reasonable attorneys' fees, arising from actions by Client that directly or indirectly implicate or affect Company (i.e., if Company is sued because Client dated a married person during the performance of Services, or Client acted in a way that was abusive to another person while participating in Services).<br/>客户应为公司、其附属公司、员工、董事、管理人员和代理商辩护，赔偿并使其免受因客户的行为直接或间接牵连或影响公司而产生的任何和所有费用、成本、损害、损失、责任和判决，包括合理的律师费（即如果公司因为客户在服务期间与已婚人士约会或客户在参与服务时以虐待他人的方式行事而被起诉）。</p>
                <p>8.2 Client shall hold harmless Company, its affiliates, employees, directors, officers, and agents in the event that Client experiences abuse, a loss of relationships, or any other negative experience while participating in the Services in perpetuity.<br/>如果客户在参与服务期间经历虐待、关系丧失或任何其他负面经历，客户应使公司、其附属公司、员工、董事、管理人员和代理商永久免受损害。</p>

                <h4 style={agH4}>Section 9. Limitation of Liability / 责任限制</h4>
                <p>9.1 In no event shall either party be liable for any indirect, incidental, special or consequential damages, incurred by either party or any third party, whether in an action in contract or tort, even if the other party has been advised of the possibility of such damages. Company's total liability for damages under the Agreement shall in no event exceed the amount of fees paid by the Client under the Agreement for the most recent one (1) year period. The provisions of the Agreement allocate the risk between Company and Client. The parties agree that Company's pricing and other terms and conditions of the Agreement reflect the allocation of risk and the limitation of liability specified herein.<br/>无论在何种情况下，任何一方均不对任何一方或第三方因合同或侵权行为而产生的任何间接、附带、特殊或后果性损害承担责任，即使另一方已被告知此类损害的可能性。根据本协议，公司对损害的总责任在任何情况下均不得超过客户在最近一（1）年期间根据本协议支付的费用。本协议的条款分配了公司和客户之间的风险。双方同意公司的定价和本协议的其他条款和条件反映了此处规定的风险分配和责任限制。</p>

                <h4 style={agH4}>Section 10. Miscellaneous / 杂项</h4>
                <p>10.1 <strong>Entire Agreement.</strong> This Agreement, together with all Schedules, (a) collectively constitute the entire agreement between the parties, and (b) supersede all prior and contemporaneous agreements, understandings, proposals and communications, oral or written, relating to the subject matter of this Agreement. Any other document or record prepared, issued, or provided by or on behalf of Client relating to the subject matter of this Agreement is for administrative convenience only and will have no effect in supplementing, varying, or superseding any provisions of this Agreement, regardless of any acknowledgement thereof by Company.<br/>本协议与所有附表一起，（a）共同构成双方之间的完整协议，并且（b）取代所有先前和同期的协议、谅解、提案和通讯，无论是口头还是书面，与本协议的主题有关。客户或其代表准备、发布或提供的任何其他文件或记录，仅为行政方便，不会对本协议的任何条款进行补充、变更或取代，无论公司是否对此表示认可。</p>
                <p>10.2 <strong>Severability.</strong> If any provision of this Agreement is held to be invalid, illegal, or unenforceable, such provision shall be construed or limited, and/or deemed replaced by a revised provision, to the extent (and only to the extent) necessary to render it valid, legal, and enforceable and, as nearly as possible, to reflect and achieve the parties' intentions in agreeing to the original provision. If it is not possible to so construe, limit, or reform any such provision, then the invalid, illegal, or unenforceable provision shall be severed from this Agreement. The remaining provisions of this Agreement shall be unaffected thereby and shall continue in full force and effect.<br/>如果本协议的任何条款被认为无效、非法或不可执行，则应在必要的范围内（仅在必要的范围内）对该条款进行解释或限制，和/或被视为替换为修订条款，尽可能反映并实现双方对原条款的意图。如果不可能如此解释、限制或修订任何此类条款，则该无效、非法或不可执行的条款应从本协议中删除。其余条款不受影响，继续完全有效。</p>
                <p>10.3 <strong>Governing Law.</strong> This Agreement shall be governed by and construed and enforced in accordance with the laws of the State of California. Both parties hereby consent to venue and jurisdiction in the federal or state courts of California.<br/>本协议应受加利福尼亚州法律管辖并按照其解释和执行。双方在此同意加利福尼亚州的联邦或州法院为管辖法院。</p>
                <p>10.4 <strong>Amendments; Waiver.</strong> Except as expressly provided above, this Agreement may not be amended, modified, superseded, or cancelled, nor may any of the terms, covenants, representations, warranties, conditions, or agreements herein be waived, except by a written instrument executed by the party against whom such amendment, modification, supersession, cancellation, or waiver is charged. No waiver by either of the parties of any condition, or of any breach of any term, covenant, representation, warranty, condition, or agreement contained herein, shall be deemed to be or shall be construed to be a waiver or continuing waiver of any such condition or breach or a waiver of any other condition or of the breach of any other term, covenant, representation, warranty, condition, or agreement hereof.<br/>除非上文明确规定，本协议不得修订、修改、取代或取消，亦不得放弃本协议中的任何条款、约定、陈述、保证、条件或协议，除非经由被指控修订、修改、取代、取消或放弃的一方签署的书面文件。任何一方对本协议中包含的任何条件或任何条款、约定、陈述、保证、条件或协议的违反的任何放弃，均不得被视为或解释为对任何此类条件或违反的放弃或继续放弃，或对任何其他条件或任何其他条款、约定、陈述、保证、条件或协议的违反的放弃。</p>
                <p>10.5 <strong>No Third-Party Beneficiary.</strong> No third party is intended to be or shall be a third-party beneficiary of any provision under this Agreement. Client and Company shall be the only parties entitled to enforce the rights set out in this Agreement.<br/>任何第三方均不被视为或应为本协议下任何条款的第三方受益人。客户和公司应是唯一有权执行本协议中规定的权利的各方。</p>
                <p>10.6 <strong>Assignment.</strong> Client may not assign or otherwise transfer all or part of this Agreement without first obtaining the express written consent of Company. Company may freely assign or otherwise transfer all or part of this Agreement.<br/>未经公司明确书面同意，客户不得转让或以其他方式转让本协议的全部或部分。公司可自由转让或以其他方式转让本协议的全部或部分。</p>
                <p>10.7 <strong>Force Majeure.</strong> Except for payment obligations, neither party will be liable to the other for any failure or delay in performing its obligations under this Agreement due to any cause beyond its reasonable control, including, without limitation, fire, flood, earthquake, or other natural catastrophes, acts of war, terrorism or civil disobedience, governmental acts, laws or regulations, embargoes, labor strikes or difficulties, failures of third party suppliers, acts or omissions of carriers, transmitters, providers of telecommunications or Internet services. Each party will use reasonable efforts to give written notice to the other promptly after becoming aware of any condition or event causing any such excusable performance failure or delay.<br/>除付款义务外，由于超出其合理控制范围的任何原因，包括但不限于火灾、洪水、地震或其他自然灾害、战争行为、恐怖主义或民众不服从、政府行为、法律或法规、禁运、劳动罢工或困难、第三方供应商的失败、承运人、传输者、电信或互联网服务提供商的行为或疏忽，任何一方均不对另一方在履行本协议的义务时的任何失败或延迟承担责任。每一方将尽合理努力在知晓导致任何此类可原谅的履约失败或延迟的情况或事件后，迅速向另一方发出书面通知。</p>
                <p>10.8 <strong>Construction.</strong> Notwithstanding any rule of construction to the contrary, any ambiguity or uncertainty in this Agreement shall not be construed against either of the parties based upon authorship of any of the provisions hereof.<br/>不论任何相反的解释规则，本协议中的任何歧义或不确定性均不得根据任何条款的作者身份对任何一方作出不利解释。</p>
                <p>10.9 <strong>Counterparts.</strong> This Agreement may be executed in several counterparts, all of which taken together shall constitute one single agreement between the parties hereto.<br/>本协议可签署多个副本，所有副本共同构成双方之间的单一协议。</p>
                <p>10.10 <strong>Attorneys' Fees.</strong> In the event of any dispute arising out of or relating to this Agreement, each party shall bear its own attorneys' fees and costs.<br/>如果因本协议引起或与之相关的任何争议，每一方应自行承担其律师费用和成本。</p>
                <p>10.11 <strong>Language.</strong> This Agreement is drafted and executed in both English and Chinese. In the event of any inconsistency or conflict between the English and Chinese versions, the English version shall prevail.<br/>本协议以英文和中文起草和签署。如果英文和中文版本之间存在任何不一致或冲突，以英文版本为准。</p>
                <p style={{ marginBottom: 0, fontWeight: 500, color: L.ink }}>IN WITNESS WHEREOF, Company and Client have each caused this Agreement to be signed by one with authority to bind, all as of the date of the last party signature.<br/>特此证明，公司和客户均已使本协议由有权约束的人员签署，以最后一方签署的日期为准。</p>
              </div>

              {/* Client-supplied profile summary — populated from the steps the
                  user just filled. Mirrors the field set the agreement refers
                  to so the signed copy is self-contained. */}
              {(() => {
                const z = formData.birthday ? getZodiacSign(formData.birthday) : null;
                const a = formData.birthday ? calculateAge(formData.birthday) : null;
                const countryName = (countries.find(c => c.code === formData.country) || {}).name || formData.country;
                const loc = [formData.city, formData.state, translateLocation(countryName || '', language)].filter(Boolean).join(', ');
                const eduLbl = (educationOptions.find(o => o.value === formData.education) || {}).label || formData.education;
                const genderLbl = (genderOptions.find(g => g.value === formData.gender) || {}).label || formData.gender;
                const rows = [
                  { k: language === 'zh' ? '姓名' : 'Name',       v: `${formData.firstName || ''} ${formData.lastName || ''}`.trim() || '—' },
                  { k: language === 'zh' ? '邮箱' : 'Email',      v: formData.email || '—' },
                  { k: language === 'zh' ? '生日' : 'Birthday',   v: formData.birthday ? (a != null ? `${formData.birthday}  (${a}${language === 'zh' ? ' 岁' : ' yrs'})` : formData.birthday) : '—' },
                  { k: language === 'zh' ? '星座' : 'Star Sign',  v: z ? `${z.symbol} ${z.sign}` : '—' },
                  { k: language === 'zh' ? '性别' : 'Gender',     v: genderLbl || '—' },
                  { k: language === 'zh' ? '居住地' : 'Location', v: loc || '—' },
                  { k: language === 'zh' ? '职业' : 'Occupation', v: formData.occupation || '—' },
                  { k: language === 'zh' ? '学历' : 'Education',  v: eduLbl || '—' },
                  { k: language === 'zh' ? '自我介绍' : 'About me', v: (formData.bio && formData.bio.trim()) || '—' },
                ];
                return (
                  <div style={{ background: L.paper2, border: `1px solid ${L.lineSoft}`, borderRadius: L.radius, padding: isDesktop ? '18px 22px' : 16, marginBottom: 20 }}>
                    <div style={kicker({ fontSize: 12, color: L.ink, marginBottom: 12 })}>
                      {language === 'zh' ? '客户填写的资料' : 'Client-provided information'}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', columnGap: 18, rowGap: 8, fontSize: 13.5 }}>
                      {rows.map((r) => (
                        <React.Fragment key={r.k}>
                          <div style={{ fontFamily: L.sans, fontSize: 13, color: MUTE, letterSpacing: '.04em' }}>{r.k}</div>
                          <div style={{ fontFamily: L.sans, color: L.ink, fontWeight: 400, wordBreak: 'break-word' }}>{r.v}</div>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <p style={{ margin: '0 0 16px', fontFamily: L.serif, fontStyle: 'italic', fontSize: 18, color: L.ink, lineHeight: 1.4 }}>
                {language === 'zh'
                  ? '点击下方电子签名即表示您同意以上条款。'
                  : 'Clicking your e-signature below constitutes your agreement to the above.'}
              </p>

              {/* Date */}
              <div style={{ marginBottom: 20, display: 'flex', alignItems: 'baseline', gap: 14, paddingBottom: 12, borderBottom: `1px solid ${L.line}` }}>
                <label style={kicker({ fontSize: 12, whiteSpace: 'nowrap' })}>
                  {language === 'zh' ? '签署日期：' : 'Signature Date:'}
                </label>
                <span style={{ fontFamily: L.serif, fontSize: 18, color: L.ink }}>
                  {new Date().toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              </div>

              {/* Signature style picker */}
              <div>
                <label style={{ ...kicker({ fontSize: 12, color: L.ink }), display: 'block', marginBottom: 12 }}>
                  {language === 'zh' ? '选择您的签名样式' : 'Select your signature style'}
                </label>
                <div role="radiogroup" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    { name: 'Elegant', fontFamily: "'Cormorant Garamond', serif" },
                    { name: 'Classic', fontFamily: 'cursive' },
                    { name: 'Formal', fontFamily: "'Playfair Display', serif" },
                    { name: 'Traditional', fontFamily: "'Georgia', serif", italic: true },
                  ].map(opt => {
                    const isSelected = signatureStyle === opt.fontFamily;
                    const fullName = `${formData.firstName || ''} ${formData.lastName || ''}`.trim()
                      || (language === 'zh' ? '请填写您的姓名' : 'Your Name');
                    return (
                      <div
                        key={opt.name}
                        role="radio"
                        aria-checked={isSelected}
                        tabIndex={0}
                        className="lx-reg-sig"
                        onClick={() => setSignatureStyle(opt.fontFamily)}
                        onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setSignatureStyle(opt.fontFamily); } }}
                        style={{
                          padding: '14px 18px', borderRadius: L.radius,
                          border: `1px solid ${isSelected ? L.ink : L.line}`,
                          background: isSelected ? L.paper : L.card,
                          cursor: 'pointer', display: 'flex',
                          alignItems: 'center', justifyContent: 'space-between', gap: 12,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontFamily: opt.fontFamily, fontStyle: opt.italic ? 'italic' : 'normal', fontSize: 28, color: L.ink, lineHeight: 1.25, wordBreak: 'break-word' }}>
                            {fullName}
                          </div>
                          <div style={kicker({ fontSize: 12, marginTop: 6 })}>
                            {language === 'zh'
                              ? ({ Elegant: '优雅', Classic: '经典', Formal: '正式', Traditional: '传统' })[opt.name]
                              : opt.name}
                          </div>
                        </div>
                        <div style={{
                          width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                          border: `1px solid ${isSelected ? L.ink : L.line}`,
                          background: isSelected ? L.ink : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: `all .3s ${L.ease}`,
                        }}>
                          {isSelected && <Check size={12} strokeWidth={2} color={L.paper} />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: isDesktop ? '18px 32px 26px' : '16px 20px 20px', borderTop: `1px solid ${L.line}`,
              display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap',
            }}>
              <button
                type="button"
                onClick={() => setShowSignatureModal(false)}
                className="lx-btn-ghost"
                style={{ ...btn.ghost, padding: '12px 22px', flex: isDesktop ? '0 0 auto' : 1 }}
              >
                {language === 'zh' ? '取消' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={() => {
                  const fullName = `${formData.firstName || ''} ${formData.lastName || ''}`.trim();
                  if (!fullName) {
                    alert(language === 'zh' ? '请先在前面的步骤填写您的姓名' : 'Please complete your name in the earlier steps first');
                    return;
                  }
                  if (!signatureStyle) {
                    alert(language === 'zh' ? '请选择一个签名样式' : 'Please choose a signature style');
                    return;
                  }
                  const data = { name: fullName, fontFamily: signatureStyle, signedAt: new Date().toISOString() };
                  setSignatureData(data);
                  setFormData({ ...formData, agreedToTerms: true });
                  setShowSignatureModal(false);
                  if (errors.agreedToTerms) setErrors({ ...errors, agreedToTerms: null });
                }}
                disabled={!signatureStyle}
                className="lx-btn"
                style={{
                  ...btn.primary, padding: '12px 22px', flex: isDesktop ? '0 0 auto' : 1,
                  opacity: signatureStyle ? 1 : 0.4, cursor: signatureStyle ? 'pointer' : 'not-allowed',
                }}
              >
                {language === 'zh' ? '电子签名并同意' : 'E-Sign & Agree'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Register;
